//import { SubcategoryHierarchy } from "@/services/api";

// Tipos de usuario
export enum UserRole {
  ADMIN = 'admin',
  USER = 'user'
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended'
}

export interface User {
  id: string;
  cedula: string;
  email?: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  geographicZone?: string;
  distribuidora: string;
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
}

// Tipos de categorías y productos
export enum ProductType {
  BLANQUEADOR = 'blanqueador',
  LIMPIADORES = 'limpiadores',
  LAVALOZAS = 'lavalozas',
  DESMANCHADORES = 'desmanchador',
  DETERGENTES = 'detergente',
  BLANCOX = 'blancox',
  ALIMENTOS = 'alimentos'

}

export interface Category {
  id: string;
  name: string;
  type: ProductType;
  description?: string;
  iconUrl?: string;
  createdAt: string;
  updatedAt: string;
  videos?: Video[];
  arContents?: ArContent[];
  subcategories: SubcategoryHierarchy[];
  testQuestions?: TestQuestion[];
}

export interface Subcategory {
  id: string;
  categoryId: string;
  name: string;
  size: string;
  iconUrl?: string;
  products?: Product[];
  createdAt: string;
  updatedAt: string;
}

export interface SubcategoryHierarchy {
  id: string;
  categoryId: string;
  parentSubcategoryId?: string;
  name: string;
  size: string;
  iconUrl?: string;
  level: number;
  createdAt: Date;
  updatedAt: Date;
  
  // Relaciones
  category?: Category;
  parent?: SubcategoryHierarchy;
  children: SubcategoryHierarchy[];
  products: Product[];
  
  // Metadata calculada
  hasChildren: boolean;
  hasProducts: boolean;
  isLeaf: boolean;
  productCount: number;
  childrenCount?: number;
  fullPath?: string;
}

export interface BreadcrumbItem {
  id: string;
  name: string;
  level: number;
}

export interface SubcategoryBreadcrumb {
  names: string[];
  ids: string[];
  depth: number;
}

export interface SubcategoryMetrics {
  totalSubcategories: number;
  totalProducts: number;
  maxDepth: number;
  leafSubcategories: number;
  averageProductsPerLeaf: number;
}

export interface SubcategoryValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  hasWarnings: boolean;
}

export interface SubcategoryTreeInfo {
  totalDescendants: number;
  maxDepth: number;
  isRequestedNode: boolean;
}

export interface CreateSubcategoryData {
  categoryId: string;
  parentSubcategoryId?: string;
  name: string;
  size: string;
  iconUrl?: string;
  iconFile?: File; // ✅ nuevo campo opcional
}

export interface UpdateSubcategoryData {
  name?: string;
  size?: string;
  iconUrl?: string;
  parentSubcategoryId?: string;
}

export interface NavigationState {
  currentSubcategories: SubcategoryHierarchy[];
  breadcrumbs: BreadcrumbItem[];
  currentLevel: number;
  isLoading: boolean;
}

export interface SubcategoryNavigationContext {
  navigationState: NavigationState;
  navigateToSubcategory: (subcategory: SubcategoryHierarchy) => Promise<void>;
  navigateToBreadcrumb: (index: number) => Promise<void>;
  resetNavigation: () => void;
}

export interface Product {
  id: string;
  subcategoryId: string;
  name: string;
  description?: string;
  imageUrl?: string;
  internalCode?: string;
  subcategory?: SubcategoryHierarchy;
  createdAt: string;
  updatedAt: string;
}

// Tipos de contenido educativo
// export interface Video {
//   id: string;
//   categoryId: string;
//   title: string;
//   youtubeUrl: string;
//   description?: string;
//   duration?: number;
//   createdAt: string;
//   updatedAt: string;
// }

export interface ArContent {
  id: string;
  categoryId: string;
  title: string;
  arUrl?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

/** Tipos de pregunta soportados por la app */
export type QuestionKind = 'drag_drop' | 'questions';

export interface TestQuestion {
  id: string;
  categoryId: string;
  question: string;               // Enunciado (para drag_drop o para formato simple de quiz)
  questionType: QuestionKind;     // 'drag_drop' | 'questions'
  options?: unknown;              // Puede ser objeto o string (JSON)
  correctAnswer?: unknown;        // string | string[] | undefined (o string JSON)
  points: number;
  createdAt: string;
  updatedAt: string;
}

// Tipos de administración de test questions
export type TestQuestionMode = 'single' | 'multi';

export interface TestAnswerOption {
  id: string;
  text: string;
}

export interface AdminTestQuestion {
  id: string;
  categoryId: string;
  question: string;
  questionType: QuestionKind;
  options: { mode: TestQuestionMode; answers: TestAnswerOption[] } | unknown;
  correctAnswer: string | string[] | unknown;
  points: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTestQuestionData {
  categoryId: string;
  question: string;
  mode: TestQuestionMode;
  answers: TestAnswerOption[];
  correctAnswer: string | string[];
  points: number;
}

export type UpdateTestQuestionData = Partial<Omit<CreateTestQuestionData, 'categoryId'>>;

// Tipos de actividades
export enum ActivityType {
  VIDEO = 'video',
  AR = 'ar',
  PORTFOLIO = 'portfolio',
  TEST = 'test'
}

export interface UserActivity {
  id: string;
  userId: string;
  categoryId: string;
  activityType: 'video' | 'ar' | 'portfolio' | 'test';
  completed: boolean;
  completionDate?: Date;
  timeSpent?: number;
  metadata?: {
    // Metadata existente
    productId?: string;
    productName?: string;
    subcategoryId?: string;
    viewedProducts?: string[];
    totalProductsViewed?: number;
    totalProducts?: number;
    
    // NUEVO: Metadata jerárquica
    breadcrumbPath?: string;
    maxLevelReached?: number;
    subcategoryPath?: BreadcrumbItem[];
    navigationHistory?: string[];
  };
  createdAt: Date;
}

export interface Certificate {
  id: string;
  userId: string;
  categoryId: string;
  certificateUrl?: string;
  issuedDate: string;
  isDownloaded: boolean;
  downloadCount: number;
  user?: User;
  category?: Category;
}

// Tipos de progreso
export interface UserProgress {
  video: boolean;
  ar: boolean;
  portfolio: boolean;
  test: boolean;
  totalTimeSpent: number;
  completedActivities: number;
  totalActivities: number;
}

// Tipos de administración
export interface UserGroup {
  id: string;
  name: string;
  description?: string;
  status: UserStatus;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  members?: UserGroupMember[];
}

export interface UserGroupMember {
  id: string;
  userId: string;
  groupId: string;
  joinedAt: string;
  user?: User;
  group?: UserGroup;
}

export interface Notification {
  id: string;
  title: string;
  body: string;
  imageUrl?: string;
  actionButtonText?: string;
  actionButtonUrl?: string;
  createdBy?: string;
  createdAt: string;
  sentAt?: string;
  emailSent?: boolean;
}

// Tipos de respuesta de API
export interface ApiResponse<T = any> {
  message?: string;
  error?: string;
  success?: boolean;
  data?: T;
}

export interface PaginatedResponse<T> {
  users: T[];
  notifications?: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Tipos de autenticación
export interface AuthResponse {
  token: string;
  user: User;
  message: string;
}

export interface LoginCredentials {
  cedula: string;
  password: string;
}

export interface RegisterData {
  cedula: string;
  name: string;
  password: string;
  email?: string;
  geographicZone?: string;
  distribuidora: string;
}

// Tipos de dashboard
export interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  completedTutorials: number;
  totalCertificates: number;
  activitiesByZone: Array<{
    zone: string;
    count: number;
  }>;
  popularModules: Array<{
    categoryName: string;
    visits: number;
  }>;
}

// Tipos de formularios
export interface CreateUserForm {
  cedula: string;
  name: string;
  password: string;
  email?: string;
  geographicZone?: string;
  distribuidora: string;
  role?: UserRole;
}

export interface NotificationForm {
  title: string;
  body: string;
  imageUrl?: string;
  actionButtonText?: string;
  actionButtonUrl?: string;
  recipients: {
    type: 'all' | 'group' | 'users';
    groupId?: string;
    userIds?: string[];
  };
}

// Tipos de modal
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

// Tipos de tutorial
export interface TutorialStep {
  id: string;
  title: string;
  description: string;
  image?: string;
  videoUrl?: string;
}

export interface Tutorial {
  id: string;
  title: string;
  description: string;
  steps: TutorialStep[];
  category?: 'general' | ProductType;
}

/** ---------- Opciones para Drag & Drop ---------- */
export interface DragDropOptions {
  items?: Array<{ id: string; text: string }>;
  dropZones?: Array<{ id: string; label: string; correctItems?: string[] }>;
}

/** ---------- Opciones para Questions (quiz) ---------- */
export type QuizQuestionsContainer =
  | Record<string, QuizSubQuestion>
  | QuizSubQuestion[];

export interface QuizOptionsRoot {
  mode?: 'single' | 'multi';
  questions?: QuizQuestionsContainer;
  // formato simple:
  answers?: Array<QuizAnswerRaw>;
}

export interface QuizSubQuestion {
  id?: string;
  title: string; // título de la sub‑pregunta
  answers: Array<QuizAnswerRaw>;
  /** Si las respuestas no traen `correct`, se puede marcar aquí:
   *  - string (id correcto, para single)
   *  - string[] (ids correctos, para multi)
   */
  correct_answer?: string | string[];
}

export interface QuizAnswerRaw {
  id: string;
  text: string;
  img?: string;
  correct?: boolean; // opcional si se usa correct_answer
}

/** ---------- Tipos UI que usa TestPage ---------- */
export interface DragDropQuestionUI {
  id: string;
  question: string;
  type: 'drag_drop';
  options: {
    items: Array<{ id: string; text: string }>;
    dropZones: Array<{ id: string; label: string; correctItems: string[] }>;
  };
  points: number;
}

export interface QuizAnswerUI {
  id: string;
  text: string;
  img?: string;
  correct?: boolean;
}

export interface QuizQuestionUI {
  id: string;
  question: string; // título para cada sub‑pregunta (o question de fila en formato simple)
  type: 'questions';
  options: {
    mode?: 'single' | 'multi';
    answers: QuizAnswerUI[];
  };
  correct_answer?: string | string[];
  points: number;
}

export type AnyQuestionUI = DragDropQuestionUI | QuizQuestionUI;

/** ---------- Type Guards útiles (opcional) ---------- */
export const isDragDrop = (q: AnyQuestionUI): q is DragDropQuestionUI =>
  q.type === 'drag_drop';

export const isQuiz = (q: AnyQuestionUI): q is QuizQuestionUI =>
  q.type === 'questions';

// Tipos para crear productos
export interface CreateProductData {
  name: string;
  description?: string;
  internalCode?: string;
  subcategoryId: string;
  imageUrl?: string;
  // === NUEVO ===
  imageFile?: File | null;
}

// Tipos para actualizar productos
export interface UpdateProductData {
  name?: string;
  description?: string;
  internalCode?: string;
  imageUrl?: string;
  // === NUEVO ===
  imageFile?: File | null;
}




// Respuestas de la API para crear
export interface CreateSubcategoryResponse {
  subcategory: SubcategoryHierarchy;
  message: string;
}

export interface CreateProductResponse {
  product: Product;
  message: string;
}


// Videos
export interface Video {
  id: string;
  categoryId: string;
  title: string;
  youtubeUrl: string;
  description?: string;
  duration?: number; // en segundos
  category?: Category;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVideoData {
  categoryId: string;
  title: string;
  youtubeUrl: string;
  description?: string;
  duration?: number;
}

export interface UpdateVideoData {
  title?: string;
  youtubeUrl?: string;
  description?: string;
  duration?: number;
}

export interface CreateVideoResponse {
  video: Video;
  message: string;
}